
import { createContext, useContext, useState, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { assessmentModules } from "@/data/assessmentModules";
import { AssessmentModule, AssessmentState } from "@/types/assessment";
import { toast } from "sonner";

interface AssessmentContextType {
  modules: AssessmentModule[];
  currentModuleId: string | null;
  assessmentState: AssessmentState;
  startAssessment: (moduleId: string) => void;
  continueAssessment: (moduleId: string) => void;
  exitAssessment: () => void;
  handleSave: () => void;
  handleComplete: () => void;
  getModuleById: (moduleId: string) => AssessmentModule | undefined;
  updateModuleStatus: (moduleId: string, status: "not-started" | "in-progress" | "complete") => void;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
}

interface AssessmentProviderProps {
  children: ReactNode;
}

export function AssessmentProvider({ children }: AssessmentProviderProps) {
  const [modules, setModules] = useState<AssessmentModule[]>(assessmentModules);
  const [currentModuleId, setCurrentModuleId] = useState<string | null>(null);
  const [assessmentState, setAssessmentState] = useState<AssessmentState>({});
  
  // Ensure module ID is always valid and consistent
  const validateModuleId = (moduleId: string | null): string | null => {
    if (!moduleId) return null;
    const isValidModule = modules.some(module => module.id === moduleId);
    if (!isValidModule) {
      return null;
    }
    return moduleId;
  };

  const getModuleById = (moduleId: string) => {
    return modules.find(module => module.id === moduleId);
  };

  const updateModuleStatus = (moduleId: string, status: "not-started" | "in-progress" | "complete") => {
    setModules(prevModules => 
      prevModules.map(module => 
        module.id === moduleId ? { ...module, status } : module
      )
    );
  };

  const startAssessment = (moduleId: string) => {
    const validatedModuleId = validateModuleId(moduleId);
    if (!validatedModuleId) {
      Sentry.captureException(new Error('Invalid module ID for startAssessment'), {
        tags: { context: 'validation_error', component: 'AssessmentContext' },
        extra: { message: `Cannot start assessment: Invalid module ID: ${moduleId}`, moduleId }
      });
      return;
    }
    
    const module = getModuleById(validatedModuleId);
    if (!module) {
      Sentry.captureException(new Error('Module not found for startAssessment'), {
        tags: { context: 'validation_error', component: 'AssessmentContext' },
        extra: { message: `Cannot start assessment: Module not found: ${validatedModuleId}`, validatedModuleId }
      });
      return;
    }

    setCurrentModuleId(validatedModuleId);
    
    // Initialize or reset assessment state for this module
    setAssessmentState(prev => ({
      ...prev,
      [moduleId]: {
        currentStep: 1,
        totalSteps: module.sections.length,
        isComplete: false,
        lastUpdated: new Date()
      }
    }));

    updateModuleStatus(moduleId, "in-progress");
    window.scrollTo(0, 0);
  };

  const continueAssessment = (moduleId: string) => {
    const validatedModuleId = validateModuleId(moduleId);
    if (!validatedModuleId) {
      Sentry.captureException(new Error('Invalid module ID for continueAssessment'), {
        tags: { context: 'validation_error', component: 'AssessmentContext' },
        extra: { message: `Cannot continue assessment: Invalid module ID: ${moduleId}`, moduleId }
      });
      return;
    }
    setCurrentModuleId(validatedModuleId);
    window.scrollTo(0, 0);
  };

  const exitAssessment = () => {
    setCurrentModuleId(null);
    window.scrollTo(0, 0);
  };


  const handleSave = () => {
    if (!currentModuleId) return;
    
    setAssessmentState(prev => ({
      ...prev,
      [currentModuleId]: {
        ...prev[currentModuleId],
        lastUpdated: new Date()
      }
    }));
    
    toast.success("Progress saved successfully");
  };

  const handleComplete = () => {
    if (!currentModuleId) return;

    setAssessmentState(prev => ({
      ...prev,
      [currentModuleId]: {
        ...prev[currentModuleId],
        isComplete: true,
        lastUpdated: new Date()
      }
    }));

    updateModuleStatus(currentModuleId, "complete");
    window.scrollTo(0, 0);
  };

  const value = {
    modules,
    currentModuleId,
    assessmentState,
    startAssessment,
    continueAssessment,
    exitAssessment,
    handleSave,
    handleComplete,
    getModuleById,
    updateModuleStatus
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
}
